/**
 * File drop zone with magic-bytes validation and optional auto-upload.
 */

import * as React from 'react';
import {
  Upload,
  FileText,
  X,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Image as ImageIcon,
  Film,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  isPendingDocumentLink,
  PENDING_DOCUMENT_LINK_PREFIX,
} from '@/utils/pending-document-link-utils';

/** Image, video, or generic document for the preview glyph. */
type DropzoneFileKind = 'image' | 'video' | 'document';

/** Pick the preview kind from mime, name, then accept. */
function resolveDropzoneFileKind(
  mimeType: string | undefined,
  fileName: string | null,
  accept: string,
): DropzoneFileKind {
  const mime = mimeType?.toLowerCase() ?? '';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  const name = (fileName ?? '').split('?')[0].toLowerCase();
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return 'image';
  if (/\.(mp4|mov|webm)$/.test(name)) return 'video';
  const tokens = accept.toLowerCase();
  if (tokens.includes('image') && !tokens.includes('video')) return 'image';
  if (tokens.includes('video') && !tokens.includes('image')) return 'video';
  return 'document';
}

/** One magic-bytes slice (`offset` + expected header bytes). */
type MagicCheck = { offset: number; bytes: number[] };

/** Magic-byte signatures used to verify uploaded file types. */
const MAGIC_CHECKS: Record<string, MagicCheck[]> = {
  pdf: [{ offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }], // %PDF
  jpeg: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }], // FF D8 FF
  png: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }], // ‰PNG
  webp: [
    { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] }, // RIFF
    { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] }, // WEBP
  ],
  mp4: [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }], // ftyp
  mov: [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }], // ftyp
  webm: [{ offset: 0, bytes: [0x1a, 0x45, 0xdf, 0xa3] }], // EBML
};

/** Maps individual accept-string tokens → magic-key(s). Unknown tokens → []. */
const ACCEPT_TOKEN_TO_MAGIC: Record<string, string[]> = {
  '.pdf': ['pdf'],
  'application/pdf': ['pdf'],
  '.jpg': ['jpeg'],
  '.jpeg': ['jpeg'],
  'image/jpeg': ['jpeg'],
  '.png': ['png'],
  'image/png': ['png'],
  '.webp': ['webp'],
  'image/webp': ['webp'],
  'image/*': ['jpeg', 'png', 'webp'],
  '.mp4': ['mp4'],
  'video/mp4': ['mp4'],
  '.mov': ['mov'],
  'video/quicktime': ['mov'],
  '.webm': ['webm'],
  'video/webm': ['webm'],
  'video/*': ['mp4', 'mov', 'webm'],
};

/** True when the file header matches a known magic for the accept tokens (unknown tokens skip). */
async function verifyMagicBytes(file: File, acceptTokens: string[]): Promise<boolean> {
  const magicKeys = [...new Set(acceptTokens.flatMap((t) => ACCEPT_TOKEN_TO_MAGIC[t] ?? []))];
  if (magicKeys.length === 0) return true; // no known magic → skip check

  const HEADER_SIZE = 12;
  const buffer = await file.slice(0, HEADER_SIZE).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  return magicKeys.some((key) => {
    const checks = MAGIC_CHECKS[key];
    if (!checks) return false;
    return checks.every(({ offset, bytes: expected }) =>
      expected.every((b, i) => bytes[offset + i] === b),
    );
  });
}

/** User-facing format + max-size hint from the `accept` string. */
function buildHintText(accept: string, maxSizeBytes: number): string {
  const tokens = accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  const hasPdf = tokens.some((t) => t === '.pdf' || t === 'application/pdf');
  const hasJpeg = tokens.some((t) => ['.jpg', '.jpeg', 'image/jpeg'].includes(t));
  const hasPng = tokens.some((t) => ['.png', 'image/png'].includes(t));
  const hasWebp = tokens.some((t) => ['.webp', 'image/webp'].includes(t));
  const hasImageWild = tokens.includes('image/*');
  const hasVideoWild = tokens.includes('video/*');
  const hasMp4 = hasVideoWild || tokens.some((t) => t === '.mp4' || t === 'video/mp4');
  const hasWebm = hasVideoWild || tokens.some((t) => t === '.webm' || t === 'video/webm');
  const hasMov = hasVideoWild || tokens.some((t) => t === '.mov' || t === 'video/quicktime');

  const imageLabels: string[] = [];
  if (hasJpeg || hasImageWild) imageLabels.push('JPG');
  if (hasPng || hasImageWild) imageLabels.push('PNG');
  if (hasWebp || hasImageWild) imageLabels.push('WebP');

  const videoLabels: string[] = [];
  if (hasMp4) videoLabels.push('MP4');
  if (hasWebm) videoLabels.push('WEBM');
  if (hasMov) videoLabels.push('MOV');

  const hasImages = imageLabels.length > 0;
  const hasVideos = videoLabels.length > 0;
  const maxMb = Math.round(maxSizeBytes / 1024 / 1024);

  let formatPart: string;
  if (hasPdf && hasImages) {
    formatPart = `Formats acceptés : PDF ou image (${imageLabels.join(', ')})`;
  } else if (hasPdf) {
    formatPart = 'Seuls les fichiers PDF sont acceptés';
  } else if (hasImages) {
    formatPart = `Formats acceptés : image (${imageLabels.join(', ')})`;
  } else if (hasVideos) {
    formatPart = `Formats acceptés : vidéo (${videoLabels.join(', ')})`;
  } else {
    formatPart = `Formats acceptés : ${accept}`;
  }

  return `${formatPart} — taille maximale : ${maxMb} Mo`;
}

/** Props for {@link FileDropzone}. */
interface FileDropzoneProps {
  onFileSelect?: (file: File | null) => void;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  className?: string;
  /** Label shown above the drop zone */
  label?: string;
  /** Show required asterisk on the label */
  required?: boolean;
  /** Override the auto-generated hint line (e.g. from accept + maxSize) */
  hint?: string;
  /** Async upload function — if provided, auto-uploads after file selection and calls onChange with the URL */
  uploadFn?: (file: File) => Promise<string>;
  /** Existing file URL to display as already-uploaded */
  value?: string;
  /**
   * Optional URL to use for the "Voir le fichier" link, independent of `value`.
   * Use when `value` is a plain filename (for display) but you have a separate
   * signed/Firebase URL to actually open the file.
   */
  previewUrl?: string;
  /** Called with the URL after a successful upload */
  onChange?: (url: string) => void;
  /** `null` when the current selection is valid; a message when it is rejected. */
  onError?: (message: string | null) => void;
  /** Pass a rejected file to `onFileSelect` so the form schema can block submit. */
  forwardRejectedFile?: boolean;
}

/** Last path segment of a URL, without query string (best-effort). */
function fileNameFromUrl(url: string): string {
  const stored = isPendingDocumentLink(url) ? url.slice(PENDING_DOCUMENT_LINK_PREFIX.length) : url;
  try {
    const decoded = decodeURIComponent(stored);
    const parts = decoded.split('/');
    const last = parts.at(-1) ?? stored;
    return last.split('?')[0] || stored;
  } catch {
    return stored;
  }
}

/** Pure function — synchronous file validation (size + extension/mime). */
function validateFileSync(
  file: File,
  accept: string,
  maxSize: number,
  hintText: string,
): string | null {
  if (file.size > maxSize) {
    const maxMb = Math.round(maxSize / 1024 / 1024);
    return getErrorMessage({ code: 'fileTooLarge', maxMb });
  }
  const acceptedTokens = accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const extTokens = acceptedTokens.filter((token) => token.startsWith('.'));
  const mimeTokens = acceptedTokens.filter((token) => !token.startsWith('.'));
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
  const extMatch = extTokens.includes(ext);
  const mimeMatch = mimeTokens.some((token) =>
    token.endsWith('/*') ? file.type.startsWith(token.slice(0, -2)) : file.type === token,
  );
  const matchesExtension = extTokens.length > 0 && extMatch;
  const matchesMime = mimeTokens.length > 0 && mimeMatch;
  if (!matchesExtension && !matchesMime && (extTokens.length > 0 || mimeTokens.length > 0)) {
    return getErrorMessage({ code: 'fileFormatRejected', hint: hintText });
  }
  return null;
}

/** State setters passed into {@link runUpload}. */
interface UploadHandlers {
  uploadFn: (file: File) => Promise<string>;
  setUploading: (v: boolean) => void;
  setUploadedUrl: (url: string) => void;
  onChange: ((url: string) => void) | undefined;
  setError: (msg: string) => void;
  setSelectedFile: (f: File | null) => void;
  onFileSelect: ((f: File | null) => void) | undefined;
}

/** Runs the upload and updates state; extracted to reduce cognitive complexity of `handleFileSelection`. */
async function runUpload(file: File, handlers: UploadHandlers): Promise<void> {
  const {
    uploadFn,
    setUploading,
    setUploadedUrl,
    onChange,
    setError,
    setSelectedFile,
    onFileSelect,
  } = handlers;
  setUploading(true);
  try {
    const url = await uploadFn(file);
    setUploadedUrl(url);
    onChange?.(url);
  } catch (err: any) {
    setError(getErrorMessage(err));
    setSelectedFile(null);
    onFileSelect?.(null);
  } finally {
    setUploading(false);
  }
}

/** Spinner shown while `uploadFn` is in flight. */
function DropzoneUploading() {
  return (
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--color-2-nuance-1)]" />
      <p className="text-sm text-muted-foreground">Upload en cours...</p>
    </div>
  );
}

/** Props for {@link DropzoneFilePreview}. */
interface DropzoneFilePreviewProps {
  readonly displayFileName: string;
  readonly selectedFile: File | null;
  readonly uploadedUrl: string | undefined;
  readonly isUploadComplete: boolean;
  readonly effectivePreviewUrl: string | undefined;
  readonly isEffectivelyDisabled: boolean;
  readonly fileKind: DropzoneFileKind;
  readonly onRemove: (e: React.MouseEvent) => void;
}

/** Lucide glyph matching the file kind — never an image thumbnail. */
function renderPreviewGlyph(
  fileKind: DropzoneFileKind,
  isUploadComplete: boolean,
): React.ReactNode {
  if (fileKind === 'document' && isUploadComplete) {
    return <CheckCircle2 className="w-7 h-7 text-green-600" />;
  }
  if (fileKind === 'image') {
    return <ImageIcon className="w-7 h-7" style={{ color: 'var(--color-2-nuance-1)' }} />;
  }
  if (fileKind === 'video') {
    return <Film className="w-7 h-7" style={{ color: 'var(--color-2-nuance-1)' }} />;
  }
  return <FileText className="w-7 h-7" style={{ color: 'var(--color-2-nuance-1)' }} />;
}

/** Open a blob preview; fall back to navigating to the URL if the popup is blocked. */
async function openPreviewUrl(url: string) {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const pdfBlob = new Blob([blob], { type: blob.type || 'application/pdf' });
    const objectUrl = URL.createObjectURL(pdfBlob);
    const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    if (!w) window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Selected-file row with name, kind icon, and remove control. */
function DropzoneFilePreview({
  displayFileName,
  selectedFile,
  uploadedUrl,
  isUploadComplete,
  effectivePreviewUrl,
  isEffectivelyDisabled,
  fileKind,
  onRemove,
}: DropzoneFilePreviewProps) {
  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div
        data-file-kind={fileKind}
        className={cn(
          'flex items-center justify-center rounded-xl w-14 h-14 overflow-hidden',
          isUploadComplete
            ? 'bg-green-100'
            : 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
        )}
      >
        {renderPreviewGlyph(fileKind, isUploadComplete)}
      </div>
      <div className="text-center flex-1">
        <p className="text-sm font-medium text-[var(--color-1-nuance-1)] mb-1 break-all max-w-xs">
          {displayFileName}
        </p>
        {selectedFile && !uploadedUrl && (
          <p className="text-xs text-muted-foreground">
            {(selectedFile.size / 1024).toFixed(2)} KB
          </p>
        )}
        {isUploadComplete && effectivePreviewUrl && (
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              await openPreviewUrl(effectivePreviewUrl);
            }}
            className="inline-flex items-center gap-1 text-xs text-[var(--color-2-nuance-1)] underline mt-1"
          >
            Voir le fichier <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>
      {!isEffectivelyDisabled && (
        <Button
          variant="delete"
          size="sm"
          icon={X}
          iconOnly
          onClick={onRemove}
          className="absolute top-3 right-3"
          aria-label="Retirer le fichier"
        />
      )}
    </div>
  );
}

/** Props for {@link DropzoneIdle}. */
interface DropzoneIdleProps {
  readonly isDragging: boolean;
  readonly isEffectivelyDisabled: boolean;
  readonly resolvedHint: string;
  readonly onBrowseClick: (e: React.MouseEvent) => void;
}

/** Empty dropzone prompt shown before a file is selected. */
function DropzoneIdle({
  isDragging,
  isEffectivelyDisabled,
  resolvedHint,
  onBrowseClick,
}: DropzoneIdleProps) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="flex items-center justify-center rounded-xl w-14 h-14 transition-all"
        style={{
          backgroundColor: isDragging
            ? 'color-mix(in srgb, var(--color-2-nuance-1) 20%, transparent)'
            : 'color-mix(in srgb, var(--color-1-nuance-3) 15%, transparent)',
        }}
      >
        <Upload
          className={cn('w-7 h-7 transition-all', isDragging && 'scale-110')}
          style={{
            color: isDragging ? 'var(--color-2-nuance-1)' : 'var(--color-1-nuance-1)',
          }}
        />
      </div>
      <div className="text-center">
        <p className="text-base font-medium text-[var(--color-1-nuance-1)] mb-2">
          {isDragging ? 'Déposez le fichier ici' : 'Glissez-déposez votre fichier ici'}
        </p>
        <p className="text-sm text-muted-foreground mb-3">ou</p>
        <Button
          variant="outline"
          size="md"
          icon={Upload}
          iconPosition="left"
          onClick={onBrowseClick}
          disabled={isEffectivelyDisabled}
        >
          Parcourir les fichiers
        </Button>
        <p className="text-xs text-muted-foreground mt-3">{resolvedHint}</p>
      </div>
    </div>
  );
}

/** Props for {@link DropzoneContent} (uploading / preview / idle). */
interface DropzoneContentProps {
  readonly uploading: boolean;
  readonly displayFileName: string | null;
  readonly selectedFile: File | null;
  readonly uploadedUrl: string | undefined;
  readonly isUploadComplete: boolean;
  readonly effectivePreviewUrl: string | undefined;
  readonly isEffectivelyDisabled: boolean;
  readonly isDragging: boolean;
  readonly resolvedHint: string;
  readonly fileKind: DropzoneFileKind;
  readonly onRemove: (e: React.MouseEvent) => void;
  readonly onBrowseClick: (e: React.MouseEvent) => void;
}

/** Dropzone body: idle prompt, upload progress, or file preview. */
function DropzoneContent({
  uploading,
  displayFileName,
  selectedFile,
  uploadedUrl,
  isUploadComplete,
  effectivePreviewUrl,
  isEffectivelyDisabled,
  isDragging,
  resolvedHint,
  fileKind,
  onRemove,
  onBrowseClick,
}: DropzoneContentProps) {
  if (uploading) {
    return <DropzoneUploading />;
  }
  if (displayFileName) {
    return (
      <DropzoneFilePreview
        displayFileName={displayFileName}
        selectedFile={selectedFile}
        uploadedUrl={uploadedUrl}
        isUploadComplete={isUploadComplete}
        effectivePreviewUrl={effectivePreviewUrl}
        isEffectivelyDisabled={isEffectivelyDisabled}
        fileKind={fileKind}
        onRemove={onRemove}
      />
    );
  }
  return (
    <DropzoneIdle
      isDragging={isDragging}
      isEffectivelyDisabled={isEffectivelyDisabled}
      resolvedHint={resolvedHint}
      onBrowseClick={onBrowseClick}
    />
  );
}

/** Dropzone for uploading files with preview and validation. */
export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  accept = '.pdf',
  maxSize = 5 * 1024 * 1024, // 5 Mo default
  disabled = false,
  className,
  label,
  required,
  hint,
  uploadFn,
  value,
  previewUrl,
  onChange,
  onError,
  forwardRejectedFile = false,
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadedUrl, setUploadedUrl] = React.useState<string | undefined>(value);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const reportError = (message: string | null) => {
    setError(message);
    onError?.(message);
  };

  React.useEffect(() => {
    setUploadedUrl(value);
  }, [value]);

  // Resolved hint text (prop override > auto-generated)
  const resolvedHint = hint ?? buildHintText(accept, maxSize);

  /** Size + mime, then magic-bytes, then optional auto-upload. */
  const handleFileSelection = async (file: File) => {
    // 1. Synchronous checks
    const syncError = validateFileSync(file, accept, maxSize, resolvedHint);
    if (syncError) {
      setSelectedFile(forwardRejectedFile ? file : null);
      onFileSelect?.(forwardRejectedFile ? file : null);
      reportError(syncError);
      return;
    }

    // 2. Magic-bytes check (async)
    const acceptTokens = accept.split(',').map((t) => t.trim().toLowerCase());
    const magicOk = await verifyMagicBytes(file, acceptTokens);
    if (!magicOk) {
      setSelectedFile(forwardRejectedFile ? file : null);
      onFileSelect?.(forwardRejectedFile ? file : null);
      reportError(
        getErrorMessage({ code: 'invalidFileType', fileKind: resolvedHint.split('·')[0].trim() }),
      );
      return;
    }

    reportError(null);
    setSelectedFile(file);
    onFileSelect?.(file);

    if (uploadFn) {
      await runUpload(file, {
        uploadFn,
        setUploading,
        setUploadedUrl,
        onChange,
        setError: reportError,
        setSelectedFile,
        onFileSelect,
      });
    }
  };

  /** Highlight the zone while a file is dragged over (ignored when disabled). */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  /** Clear the drag highlight. */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /** Required so the browser treats this element as a drop target. */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /** Validate and optionally upload the first dropped file. */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) void handleFileSelection(files[0]);
  };

  /** Same pipeline as drop, from the hidden file input. */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) void handleFileSelection(files[0]);
  };

  /** Open the hidden file picker unless disabled or uploading. */
  const handleClick = () => {
    if (!disabled && !uploading && fileInputRef.current) fileInputRef.current.click();
  };

  /** Clear selection + uploaded URL without triggering the file picker. */
  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setUploadedUrl(undefined);
    reportError(null);
    onFileSelect?.(null);
    onChange?.('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /** True if the dropzone is effectively disabled (disabled or uploading). */
  const isEffectivelyDisabled = disabled || uploading;
  /** Display filename (from uploaded URL or selected file). */
  const displayFileName = uploadedUrl ? fileNameFromUrl(uploadedUrl) : (selectedFile?.name ?? null);
  /** True once a stored URL or an independent preview URL is available. */
  const isUploadComplete = !!(uploadedUrl || previewUrl) && !uploading;
  /** Signed/Firebase URL preferred over the stored filename/`value`. */
  const storedPreviewUrl = isPendingDocumentLink(uploadedUrl) ? undefined : uploadedUrl;
  const effectivePreviewUrl = previewUrl ?? storedPreviewUrl;
  const fileKind = resolveDropzoneFileKind(selectedFile?.type, displayFileName, accept);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="form-label block mb-2">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        className="sr-only"
        disabled={isEffectivelyDisabled}
      />

      <button
        type="button"
        disabled={isEffectivelyDisabled}
        onKeyDown={(e) => {
          if (!isEffectivelyDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative w-full rounded-xl border-2 border-dashed transition-all duration-200',
          'min-h-[200px] flex flex-col items-center justify-center p-8',
          !isEffectivelyDisabled &&
            !isDragging &&
            !error &&
            'hover:border-[var(--color-2-nuance-1)] hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_5%,transparent)]',
          isDragging &&
            !isEffectivelyDisabled &&
            'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] scale-[1.02]',
          isUploadComplete && 'border-green-400 bg-green-50/30',
          error && 'border-[rgb(var(--color-error))] bg-[rgb(var(--color-error)_/_0.05)]',
          !error && !isDragging && !isUploadComplete && 'border-[var(--color-1-nuance-3)]',
          isEffectivelyDisabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <DropzoneContent
          uploading={uploading}
          displayFileName={displayFileName}
          selectedFile={selectedFile}
          uploadedUrl={uploadedUrl}
          isUploadComplete={isUploadComplete}
          effectivePreviewUrl={effectivePreviewUrl}
          isEffectivelyDisabled={isEffectivelyDisabled}
          isDragging={isDragging}
          resolvedHint={resolvedHint}
          fileKind={fileKind}
          onRemove={handleRemoveFile}
          onBrowseClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        />
      </button>

      {error && (
        <div className="mt-3 p-3 bg-[rgb(var(--color-error)_/_0.08)] border border-[rgb(var(--color-error)_/_0.3)] rounded-lg">
          <p className="text-sm text-[rgb(var(--color-error))] font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};
